import { effect, signal, type DestroyRef, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type Observable, type Subscription } from 'rxjs';

/**
 * Takes as input a Signal that carries an Observable. This Signal allows switching between different streams of observables.
 * The function returns a unique Signal that is updated with each emission from the current input observable stream.
 *
 * Functionality:
 * When a new observable is emitted by the input Signal, the function unsubscribes from the previous observable (if it exists)
 * and subscribes to the new observable. With each new value emitted by this observable, the value of the return Signal is updated.
 * This allows managing the change of the emitting stream while producing a unique output stream.
 */
export function subscribeToMultiObservable<T>(
  signalToTransform: Signal<Observable<T>>,
  destroyRef?: DestroyRef,
): Signal<T> {
  let subscription: Subscription | null = null;

  const signalForReturn = signal<T>(null);

  effect(() => {
    subscription?.unsubscribe();

    if (!signalToTransform()) {
      return;
    }

    const observable = destroyRef
      ? signalToTransform().pipe(takeUntilDestroyed(destroyRef))
      : signalToTransform();

    subscription = observable.subscribe(value => signalForReturn.set(value));
  });

  return signalForReturn;
}
